import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Model } from '@df/catalog/model';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import last from 'lodash-es/last';

export interface PageComponentInstagramFeedProductPageData extends NICmsContentComponent.IData {
    snappptUrl?: string;
    title?: string;
    titleLink?: string;
    subtitle?: string;
    ctaText?: string;
    ctaLink?: string;
}
@ICmsComponent<PageComponentInstagramFeedProductPageComponent>({
    id: 'instagram-feed-product-page',
    name: 'Instagram feed product page',
    tags: ['UGC', 'instagram', 'snapppt'],
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IField>{
            key: 'snappptUrl',
            type: NICmsContentField.EFieldType.text,
            title: 'Snapppt URL'
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Lorem ipsum'
        },
        <NICmsContentField.IField>{
            key: 'titleLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Title Link'
        },
        <NICmsContentField.IField>{
            key: 'subtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            default: 'Lorem ipsum dolor sit amet'
        },
        <NICmsContentField.IField>{
            key: 'ctaText',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Text',
            default: 'Follow us'
        },
        <NICmsContentField.IField>{
            key: 'ctaLink',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Link'
        }
    ]
})
@Component({
    selector: 'page-component-instagram-feed-product-page',
    templateUrl: './instagram-feed-product-page.html',
    styleUrls: ['../instagram-feed/instagram-feed.scss'],
    standalone: true,
    imports: [SizeClassDirective, HrefComponent, ICmsClientModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentInstagramFeedProductPageComponent
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnInit, OnDestroy, OnChanges
{
    @ICmsContent({
        type: 'component'
    })
    component!: PageComponentInstagramFeedProductPageData;

    @ViewChild('instagramFeedRefContainer', { static: true, read: ElementRef })
    instagramFeedRefContainer!: ElementRef<HTMLElement>;

    private lastContext?: Model;

    private timeout?: number;

    constructor(
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        private meta: Meta,
        private app: App
    ) {
        super();
    }
    override ngOnInit(): void {
        super.ngOnInit();
        if (!this.app.isPlatformBrowser) {
            return;
        }
        this.subscriptions.add(this.meta.subscribe(() => this.reset()));
        this.reset();
    }

    override ngOnChanges(changes): void {
        this.reset();
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        super.ngOnDestroy();
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private reset = this.timeoutService.debounce(() => {
        if (!this.component) {
            return;
        }
        const context = this.meta.model;
        if (this.lastContext === undefined || this.lastContext !== context) {
            this.lastContext = context;
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            const container = this.instagramFeedRefContainer.nativeElement;
            container.innerHTML = '';
            // const scritDiv = this.renderer2.createElement('div');
            // this.renderer2.appendChild(container, scritDiv);
            const script = this.renderer2.createElement('script');
            script.type = 'text/javascript';
            script.src = this.component.snappptUrl || 'https://snapppt.com/widgets/ppg/d5bb9e25-a67e-4e83-a139-69948082924f';
            script.classList.add('snapppt-widget');
            script.setAttribute('data-custom-visit-url', this.app.url.toUrl('/PRODUCT_HANDLE'));
            if (context?.type === Product.type) {
                const parts = (context as Product).parent.data!.url?.split('/');
                if (parts) {
                    script.setAttribute('data-handle', last(parts));
                }
            }
            this.renderer2.appendChild(container, script);
        }
    }, 200);
}
